import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { DateTime } from 'luxon'
import { FormikProvider, useFormik } from 'formik'
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons'
import { faUser } from '@fortawesome/pro-solid-svg-icons'
import { getFetchError } from '@/reducers/fetch'
import { getCurrentUser } from '@/selectors/user.selectors'
import { UPDATE_USER_KEY, updateUser } from '@/actions/userActions'
import { fetchUserDocuments } from '@/features/user/your-practice/documents/lib/userDocuments.slice'
import { DATE_FORMATS_LUXON, formatISOFromUTC } from '@/utils/dateHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'

import {
  Alert,
  Card,
  Icon,
  Heading,
  HeadingVariants,
  Breadcrumb,
  Form,
} from '@/ui'

import {
  FormikLocationSearchInput,
  getFieldNames,
  makeDateSchema,
  makeReqEmailSchema,
  makeReqPhoneNumberSchema,
  makeReqStringSchema,
} from '@/base'

import { PracticeProfileFormCta } from './PracticeProfileFormCta'
import { fetchUserDocumentCategoriesIfNeeded } from '@/features/admin/user-document-categories/userDocumentCategories.slice'

export const PersonalInformationForm = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const user = useReselector(getCurrentUser)
  const error = useReselector(getFetchError, UPDATE_USER_KEY)

  useEffect(() => {
    dispatch(fetchUserDocuments())
    dispatch(fetchUserDocumentCategoriesIfNeeded())
  }, [dispatch])

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstName: user?.firstName,
      lastName: user?.lastName,
      dateOfBirth:
        (user?.dateOfBirth && formatISOFromUTC(user.dateOfBirth)) || '',
      personalEmail: user?.personalEmail,
      personalPhone: user?.personalPhone,
      homeAddress: user?.homeAddress,
    },

    onSubmit: async ({
      firstName,
      lastName,
      dateOfBirth,
      personalEmail,
      personalPhone,
      homeAddress,
    }) => {
      const res = await dispatch(
        updateUser(user?.id, {
          firstName,
          lastName,
          dateOfBirth: dateOfBirth
            ? DateTime.fromFormat(dateOfBirth, DATE_FORMATS_LUXON.INPUT)
                .toUTC()
                .startOf('day')
                .toISO()
            : null,
          personalEmail: personalEmail || null,
          personalPhone: personalPhone || null,
          homeAddress: homeAddress || null,
        })
      )

      if (res) {
        navigate('/practice/profile')
      }
    },
  })

  const { submitForm, isValid, isSubmitting } = formik
  const fieldNames = getFieldNames(formik)

  return (
    <div className="flex flex-col gap-2 px-4">
      <Breadcrumb>
        <Breadcrumb.Item>
          <Breadcrumb.Link
            onClick={() => {
              navigate('/practice/profile')
            }}
          >
            <Icon icon={faArrowLeft} />
            Back to Practice Profile
          </Breadcrumb.Link>
        </Breadcrumb.Item>
      </Breadcrumb>

      <div className="flex flex-row gap-2 items-center mt-4">
        <Icon
          icon={faUser}
          backgroundStyle="rounded"
          backgroundColor="priority2"
          size="sm"
        />
        <Heading variant={HeadingVariants.H2}>Personal Information</Heading>
      </div>

      <Card>
        <FormikProvider value={formik}>
          <div className="flex flex-col gap-4">
            {error && (
              <div className="flex">
                <Alert variant="error">{error.message}</Alert>
              </div>
            )}

            <div className="flex-1 w-full">
              <Form.Input
                name={fieldNames.firstName}
                label="First Name"
                schema={makeReqStringSchema({ field: 'first name' })}
              />
            </div>
            <div className="flex-1">
              <Form.Input
                name={fieldNames.lastName}
                label="Last Name"
                schema={makeReqStringSchema({ field: 'last name' })}
              />
            </div>
            <div className="flex-1 ">
              <Form.DateInput
                name={fieldNames.dateOfBirth}
                label="Date of Birth"
                fullWidth
                schema={makeDateSchema()}
                maxDate={new Date()}
              />
            </div>
            <div className="flex-1 ">
              <Form.Input
                name={fieldNames.personalEmail}
                label="Personal Email"
                type="email"
                schema={makeReqEmailSchema({ required: false })}
              />
            </div>
            <div className="flex-1 ">
              <Form.Input
                name={fieldNames.personalPhone}
                label="Personal Phone"
                componentType="phone"
                schema={makeReqPhoneNumberSchema({ required: false })}
              />
            </div>
            <div className="flex-1">
              <FormikLocationSearchInput
                name={fieldNames.homeAddress}
                label="Personal Address"
                fullWidth
              />
            </div>
            <div className="h-2" />
            <PracticeProfileFormCta
              submitForm={submitForm}
              isValid={isValid}
              isSubmitting={isSubmitting}
            />
          </div>
        </FormikProvider>
      </Card>
    </div>
  )
}
